import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  RadioGroup,
  Radio,
  FormControlLabel,
  Autocomplete,
  Grid,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import Header from "../components/Admin/header/Header";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useGetApi } from "../hooks/useGetApi";
import { getPatientByIdApi } from "../services/patient.service";
import { getUserApi } from "../services/user.service";
import dayjs from "dayjs";
import {
  DatePicker,
  renderTimeViewClock,
  TimePicker,
} from "@mui/x-date-pickers";
import { getUnitApi } from "../services/unit.service";
import { getVisitTypeApi } from "../services/visitType.service";
import DiscountModal from "../components/consultation/modal/discount-modal";
import { getTransactionTypeApi } from "../services/transaction-type.service";
import Loader from "../components/Loader/loader";
import ErrorMessage from "../components/ErrorMessage/errorMessage";
import { toast } from "react-toastify";
import { createConsultationApi, checkFollowUpEligibilityApi } from "../services/consultation.service";
import { getCategoryApi } from "../services/category.service";
import { getWardApi } from "../services/ward.service";
import {
  getBedNumberApi,
  getNursingBedNumberApi,
} from "../services/bed-number.service";
import { CAPITALIZE } from "../utils/constants";
import BulkWaiverModal from "../components/consultation/modal/bulk-waiver-modal";
import ServiceRow from "../components/consultation/ServiceRow";
import { getRelationshipApi } from "../services/relationship.service";
import ConsultationFormSchema, {IPDSchema, OutsiderSchema} from "../joi/consultation/consultation-form.schema";
import { ArrowLeft } from "lucide-react";
import { getBedSubCategoryApi } from "../services/bed-sub-category.service";
import {
  getConsultationDatePickerBounds,
  isBackdatedConsultationDate,
  validateConsultationDateClient,
} from "../utils/consultationDateRules";
import {
  isEmptyOrValidPhone,
  phoneTextFieldInputProps,
  sanitizePhoneInput,
} from "../utils/phoneInput";

const PAYMENT_FIELD_ERROR_KEYS = [
  "transactionType",
  "paymentType",
  "amountPaid",
  "amount",
];

const SERVICE_FIELD_ERROR_KEYS = ["category", "item", "services"];

const REQUIRED_FIELDS_TOAST = "Please fill all required fields.";

const joiDetailsToFieldErrors = (details) => {
  const keys = new Set();
  details.forEach((err) => {
    (err.path || []).forEach((segment) => {
      if (typeof segment === "string") keys.add(segment);
    });
    if (typeof err.context?.key === "string") keys.add(err.context.key);
  });
  return Array.from(keys);
};

/** Red border only — keep label text normal (asterisk stays red via InputLabelProps). */
const outlinedRequiredErrorSx = (hasError) =>
  hasError
    ? {
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "error.main",
        },
        "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: "error.main",
        },
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: "error.main",
        },
      }
    : {};

const ConsultationForm = () => {
  const navigate = useNavigate();
  const { patientId } = useParams();
  const user = useSelector((state) => state.user);
  
  // Check if user has consultation permissions
  useEffect(() => {
    if (!user?.permissions || user.permissions.length === 0) {
      navigate('/patient');
      return;
    }
    
    const hasConsultationAccess = user.permissions.includes('Consultation') || 
                                 user.permissions.includes('Consultations.Create') ||
                                 user.role === 'Super Admin' ||
                                 user.role === 'Admin';
    
    if (!hasConsultationAccess) {
      navigate('/patient');
    }
  }, [user, navigate]);

  const [consultationType, setConsultationType] = useState("OPD");
  const [consultationDate, setConsultationDate] = useState(
    dayjs().format("YYYY-MM-DD")
  );
  const [consultationTime, setConsultationTime] = useState(
    dayjs().format("HH:mm")
  );
  const [errors, setErrors] = useState([]);
  const [backdateReason, setBackdateReason] = useState("");

  const [patientData, setPatientData] = useState(null);
  const [isLoadingPatient, setIsLoadingPatient] = useState(false);
  const [isErrorPatient, setIsErrorPatient] = useState(false);

  const getValidISODateTime = (date, time) => {
    if (!date || !time) return null;
    const combined = `${date}T${time}:00`;
    const parsed = new Date(combined);
    return isNaN(parsed.getTime()) ? null : parsed.toISOString();
  };

  const initialState = {
    UHID: "",
    patientId,
    unit: null,
    doctor: null,
    visitType: null,
    consultationCharge: "",
    consultationType,
    consultationDateTime: getValidISODateTime(
      consultationDate,
      consultationTime
    ),
    doctorNotes: "",
    referral: {
      doctorName: null,
      contactNumber: "",
      reason: "",
      date: dayjs(),
    },
    discount: {
      discountReason: "",
      approvedBy: null,
      discountType: "Percentage",
      discountAmount: 0,
      discountPercentage: 0,
    },
    payment: {
      transactionType: null,
      transactionId: null,
      amountPaid: 0,
      remarks: "",
      paymentType: "",
    },
    services: [
      {
        id: "1",
        category: null,
        item: null,
        quantity: 1,
        assignedDoctor: null,
        amount: 0,
        waiver: {
          waiverReason: "",
          approvedBy: "",
          waiverType: "Percentage",
          waiverAmount: 0,
          waiverPercentage: 0,
        },
      },
    ],
    bedOccupancy: {
      ward: null,
      bed: null,
    },
    emergencyContact: {
      name: "",
      relationship: null,
      contactNumber: "",
    },
  };

  // Determine the highest id from initial services and set the next ID
  const initialMaxId = Math.max(
    ...initialState.services?.map((s) => parseInt(s?.id, 10)),
    0
  );
  const nextIdRef = useRef(initialMaxId + 1);

  const [formData, setFormData] = useState(initialState);
  const [amount, setAmount] = useState('');
  const [confirmAmountPaid, setConfirmAmountPaid] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [bulkWaiverModalOpen, setBulkWaiverModalOpen] = useState(false);
  const [selectedServices, setSelectedServices] = useState([]);
  const [isTransactionTypeSet, setIsTransactionTypeSet] = useState(false);
  const navigation = useNavigate();

  // Updated validation logic to allow partial payments for "Pending Payment" transaction type and advance payments
  const isPendingPayment = formData?.payment?.transactionType?.transactionMode === "Pending Payment";
  const isAdvancePayment = formData?.payment?.paymentType === "IPDAdvance";
  
  // Smart follow-up detection - check if it's actually a valid follow-up
  const [isValidFollowUp, setIsValidFollowUp] = useState(false);
  const [isCheckingFollowUp, setIsCheckingFollowUp] = useState(false);
  
  // Check if visit type name suggests it might be a follow-up (for UI display purposes)
  const isPotentialFollowUpVisitType = formData?.visitType?.name === "OPD Old" || 
                                      formData?.visitType?.name === "Old OPD" ||
                                      formData?.visitType?.name === "Old" ||
                                      formData?.visitType?.name?.toLowerCase().includes("opd old") ||
                                      formData?.visitType?.name?.toLowerCase().includes("old");
  
  // Free follow-up (₹0) from API — separate from backdate / allowsBackdate on visit type
  const isFreeFollowUp = isValidFollowUp;

  const allowsBackdate = Boolean(formData?.visitType?.allowsBackdate);
  const datePickerBounds = getConsultationDatePickerBounds(allowsBackdate);
  const showBackdateReason =
    consultationType === "OPD" &&
    isBackdatedConsultationDate(consultationDate);

  const amountsMatch = isFreeFollowUp ? true : (isPendingPayment || isAdvancePayment
    ? (Number(confirmAmountPaid) >= 0 && Number(confirmAmountPaid) <= Number(formData?.payment?.amountPaid))
    : formData?.payment?.amountPaid === Number(confirmAmountPaid));

  const netExpense =
    consultationType === "OPD"
      ? Number(amount) || 0
      : formData?.services?.reduce((total, service) => {
          const normalRate = service?.amount
            ? service.amount
            : service?.item?.normalRate ?? 0;
          return total + normalRate;
        }, 0);

  const amountToBePaid =
    consultationType === "OPD"
      ? netExpense
      : formData?.services?.reduce((total, service) => {
          const normalRate = service?.amount
            ? service.amount
            : service?.item?.normalRate ?? 0;
          const waiverPercentage = service?.waiver?.waiverPercentage ?? 0;
          const waiverAmount =
            service?.waiver?.waiverType === "Percentage"
              ? (normalRate * waiverPercentage) / 100
              : service?.waiver?.waiverAmount ?? 0;

          return total + normalRate - waiverAmount;
        }, 0);
  // Apply discount if discountAmount or discountPercentage is greater than 0
  const finalAmountToBePaid =
    formData?.discount?.discountAmount > 0 ||
    formData?.discount?.discountPercentage > 0
      ? netExpense -
        (formData?.discount?.discountType === "Percentage"
          ? (netExpense * (formData?.discount?.discountPercentage ?? 0)) / 100
          : formData?.discount?.discountAmount ?? 0)
      : amountToBePaid;

  const formattedAmountToBePaid = Number(finalAmountToBePaid || 0).toFixed(2);

  const { dataList: doctorsList } = useGetApi({
    apiFunction: getUserApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
    dataKey: "users",
    showErrorToast: false,
  });

  const { dataList: unitList } = useGetApi({
    apiFunction: getUnitApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
    dataKey: "units",
    showErrorToast: false,
  });

  const { dataList: visitTypeList } = useGetApi({
    apiFunction: getVisitTypeApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
    dataKey: "visitTypes",
    showErrorToast: false,
  });

  const { dataList: categoryList } = useGetApi({
    apiFunction: getCategoryApi,
    params: { limit: Number.MAX_SAFE_INTEGER, type: "category" },
    dataKey: "categories",
    showErrorToast: false,
  });

  const { dataList: wardList } = useGetApi({
    apiFunction: getWardApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
    dataKey: "wards",
    showErrorToast: false,
  });

  const { dataList: nursingBedNumberList } = useGetApi({
    apiFunction: formData?.bedOccupancy?.ward?._id
      ? getNursingBedNumberApi
      : null,
    params: {
      limit: Number.MAX_SAFE_INTEGER,
      bedStatus: "Available",
      wardName: formData?.bedOccupancy?.ward?.wardName,
    },
    dependencies: [formData?.bedOccupancy?.ward?._id],
    dataKey: "bedNumbers",
    showErrorToast: false,
  });

  const { dataList: transactionList } = useGetApi({
    apiFunction: getTransactionTypeApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
    showErrorToast: false,
  });

  const { dataList: relationshipList } = useGetApi({
    apiFunction: getRelationshipApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
    dataKey: "relationships",
    showErrorToast: false,
  });

  const transactionTypeList = [
    ...(transactionList?.find((option) => option?.transactionMode === "Payment")?.transactionTypes || []),
    ...(transactionList?.find((option) => option?.transactionMode === "Pending Payment")?.transactionTypes || [])
  ];

  const { dataList: bedSubCategoryList } = useGetApi({
    apiFunction: getBedSubCategoryApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
    dataKey: "bedSubCategories",
    showErrorToast: false,
  });
  const { dataList: bedNumberList } = useGetApi({
    apiFunction: formData?.bedOccupancy?.ward?._id
      ? getNursingBedNumberApi
      : null,
    params: {
      limit: Number.MAX_SAFE_INTEGER,
      bedStatus: "Available",
      wardName: formData?.bedOccupancy?.ward?.wardName,
    },
    dependencies: [formData?.bedOccupancy?.ward?._id],
    dataKey: "bedNumbers",
    showErrorToast: false,
  });

  // Filter available beds
  const availableBedNumbers = bedNumberList?.filter(bed => bed.status === "Available") || [];

  const handleConsultationType = (e) => {
    setConsultationType(e.target.value);
    setAmount(0);
  };

  const handleModalOpen = () => {
    // Check if any service in the formData has a waiver amount or percentage greater than 0
    const hasWaiver = formData?.services?.some(
      (service) =>
        service?.waiver?.waiverAmount > 0 ||
        service?.waiver?.waiverPercentage > 0
    );

    // If there is a waiver present, show an error message and exit the function
    if (hasWaiver) {
      toast.error("Please remove the waiver before adding overall discount.");
      return;
    }

    // If no waiver is present, proceed to open the modal
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleBulkWaiverModalOpen = () => {
    const hasDiscount =
      formData?.discount?.discountAmount > 0 ||
      formData?.discount?.discountPercentage > 0;

    if (hasDiscount) {
      toast.error(
        "Please remove the overall discount before adding bulk waiver."
      );
      return;
    }
    setBulkWaiverModalOpen(true);
  };

  const handleBulkWaiverModalClose = () => {
    setBulkWaiverModalOpen(false);
  };

  const toggleRowSelection = (serviceId) => {
    setSelectedServices((prevSelected) => {
      if (prevSelected.includes(serviceId)) {
        return prevSelected.filter((id) => id !== serviceId); // Deselect
      } else {
        return [...prevSelected, serviceId]; // Select
      }
    });
  };

  const handleInputChange = (e, key) => {
    const { name, value, type, checked } = e.target;
    let parsedValue =
      type === "checkbox" ? checked : type === "number" ? Number(value) : value;

    if (
      (key === "referral" || key === "emergencyContact") &&
      name === "contactNumber"
    ) {
      parsedValue = sanitizePhoneInput(value);
    }

    if (key) {
      setFormData((preValue) => ({
        ...preValue,
        [key]: {
          ...preValue?.[key],
          [name]: parsedValue,
          ...(name === "ward" && { bed: null }),
        },
      }));
      if (key === "payment") {
        setErrors((prev) =>
          prev.filter((k) => !PAYMENT_FIELD_ERROR_KEYS.includes(k))
        );
      }
      return;
    }

    setFormData((preValue) => ({
      ...preValue,
      [name]: parsedValue,
    }));
  };

  const handleDoctorUnitChange = (e) => {
    const { name, value } = e.target;

    if (name === "doctor") {
      setFormData((preValue) => ({
        ...preValue,
        [name]: value,
        unit: value?.unit,
      }));
    }

    if (name === "unit") {
      setFormData((preValue) => ({
        ...preValue,
        [name]: value,
        doctor: null,
      }));
    }
  };

  const handleServicesChange = (e, index, key) => {
    const { name, value, type, checked } = e.target;
    const parsedValue =
      type === "checkbox" ? checked : type === "number" ? Number(value) : value;

    if (key) {
      const hasDiscount =
        formData?.discount?.discountAmount > 0 ||
        formData?.discount?.discountPercentage > 0;
      if (hasDiscount) {
        toast.error("Please remove the overall discount before adding waiver.");
        return;
      }
    }

    setFormData((prev) => {
      const updatedServicesDetails = prev?.services?.map((service, i) => {
        if (i === index) {
          if (key) {
            const updatedWaiver = { ...service?.waiver, [name]: parsedValue };

            // Conditional logic for waiverAmount and waiverPercentage
            if (name === "waiverType") {
              if (parsedValue === "Percentage" && updatedWaiver?.waiverAmount) {
                updatedWaiver.waiverAmount = 0;
              } else if (
                parsedValue === "Amount" &&
                updatedWaiver?.waiverPercentage
              ) {
                updatedWaiver.waiverPercentage = 0;
              }
            }

            return { ...service, waiver: updatedWaiver };
          }
          return {
            ...service,
            [name]: parsedValue,
            ...(name === "category" && { item: null }),
            ...(name === "item" && {
              amount: Number(parsedValue?.normalRate) || 0,
            }),
          };
        }
        return service;
      });

      return { ...prev, services: updatedServicesDetails };
    });
    setErrors((prev) =>
      prev.filter((k) => !SERVICE_FIELD_ERROR_KEYS.includes(k))
    );
  };

  const addServicesDetail = () => {
    const newId = nextIdRef.current.toString(); // Generate new id as a string
    nextIdRef.current += 1; // Increment for the next service

    const newService = {
      id: newId,
      category: null,
      item: null,
      quantity: 1,
      assignedDoctor: "",
      amount: 0,
      waiver: {
        waiverReason: "",
        approvedBy: "",
        waiverType: "Percentage",
        waiverAmount: 0,
        waiverPercentage: 0,
      },
    };

    setFormData((prev) => ({
      ...prev,
      services: [...prev.services, newService],
    }));
  };

  const removeServicesDetail = (serviceId) => {
    setFormData((prev) => ({
      ...prev,
      services: prev?.services?.filter((s) => s?.id !== serviceId),
    }));
    setSelectedServices((prevSelected) =>
      prevSelected.filter((id) => id !== serviceId)
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isEmptyOrValidPhone(formData?.referral?.contactNumber)) {
      toast.error("Referral contact number must be exactly 10 digits.");
      return;
    }

    if (
      consultationType !== "IPD" &&
      formData?.payment?.transactionType &&
      formattedAmountToBePaid !== formData?.payment?.amountPaid?.toFixed(2)
    ) {
      // Skip amount validation for OPD Old visit type
      if (!isFreeFollowUp) {
        toast.error("Amount and Amount to be paid doesn't match.");
        return;
      }
    }

    if (!amountsMatch && !isFreeFollowUp) {
      toast.error("Amount and Confirm Amount doesn't match.");
      return;
    }
    
    if (
      consultationType === "OPD" &&
      (!consultationDate || !consultationTime)
    ) {
      toast.error("Please select an consulation date and time.");
      return;
    }

    if (consultationType === "OPD") {
      const dateCheck = validateConsultationDateClient({
        consultationDate,
        allowsBackdate,
        backdateReason,
      });
      if (!dateCheck.ok) {
        toast.error(dateCheck.message);
        return;
      }
    }

    const formDataWithId = {
      ...formData,
      consultationCharge: amount,
      amount,
      consultationType,
      consultationDateTime: getValidISODateTime(
        consultationDate,
        consultationTime
      ),
      ...(showBackdateReason && backdateReason?.trim()
        ? { backdateReason: backdateReason.trim() }
        : {}),
      visitType: formData?.visitType?._id,
      doctor: formData?.doctor?._id,
      unit: formData?.unit?._id,
      // Only include payment for non-OPD Old visit types
      ...(isFreeFollowUp ? {} : {
        payment: {
          ...formData?.payment,
          transactionType:
            (typeof formData?.payment?.transactionType === "object"
              ? formData?.payment?.transactionType?._id
              : formData?.payment?.transactionType) || "",
        }
      }),
      referral: {
        ...formData?.referral,
        doctorName:
          (typeof formData?.referral?.doctorName === "object"
            ? formData?.referral?.doctorName?._id
            : formData?.referral?.doctorName) || "",
      },
      bedOccupancy: {
        ...formData?.bedOccupancy,
        ward:
          typeof formData?.bedOccupancy?.ward === "object"
            ? formData?.bedOccupancy?.ward?._id
            : formData?.bedOccupancy?.ward,
        bed:
          typeof formData?.bedOccupancy?.bed === "object"
            ? formData?.bedOccupancy?.bed?._id
            : formData?.bedOccupancy?.bed,
      },
      services: formData?.services?.map((service) => ({
        ...service,
        category:
          typeof service?.category === "object"
            ? service?.category?._id
            : service?.category,
        item:
          typeof service?.item === "object"
            ? service?.item?._id
            : service?.item,
        assignedDoctor:
          typeof service?.assignedDoctor === "object"
            ? service?.assignedDoctor?._id
            : service?.assignedDoctor,
      })),
      emergencyContact: {
        ...formData?.emergencyContact,
        relationship:
          typeof formData?.emergencyContact?.relationship === "object"
            ? formData?.emergencyContact?.relationship?._id
            : formData?.emergencyContact?.relationship,
      },
    };

    // Remove transferBankAccount from payment before validation and submission
    if (formDataWithId.payment && 'transferBankAccount' in formDataWithId.payment) {
      delete formDataWithId.payment.transferBankAccount;
    }

    // For OPD Old visit type, do not force amount to 0; keep user-entered amount/payment

    if (consultationType === "OPD") {
      delete formDataWithId?.bedOccupancy;
      delete formDataWithId?.services;
      delete formDataWithId?.emergencyContact;
    }

    if (consultationType === "Outsider") {
      delete formDataWithId?.bedOccupancy;
      delete formDataWithId?.visitType;
      delete formDataWithId?.doctor;
      delete formDataWithId?.unit;
      delete formDataWithId?.consultationDateTime;
      delete formDataWithId?.emergencyContact;
    }
    if (consultationType === "IPD") {
      delete formDataWithId?.visitType;
      delete formDataWithId?.consultationDateTime;
    }
    
    // Validate the form data using Joi
    if (consultationType === "IPD") {
      const { error } = IPDSchema.validate(formDataWithId, {
        abortEarly: false,
      });
      if (error) {
        setErrors(joiDetailsToFieldErrors(error.details));
        toast.error(REQUIRED_FIELDS_TOAST);
        return;
      }
    } 
    else if (consultationType === "Outsider") {
      const { error } = OutsiderSchema.validate(formDataWithId, {
        abortEarly: false,
      });
      if (error) {
        setErrors(joiDetailsToFieldErrors(error.details));
        toast.error(REQUIRED_FIELDS_TOAST);
        return;
      }
    } else if (consultationType === "OPD") {
      // Skip Joi validation for OPD Old visit type since they don't need amount/payment validation
      if (isFreeFollowUp) {
        // Manual validation for OPD Old visit type
        if (!formDataWithId.unit || !formDataWithId.doctor || !formDataWithId.visitType || !formDataWithId.consultationDateTime) {
          toast.error('Please fill all the required fields (Unit, Doctor, Visit Type, Date & Time).');
          return;
        }
      } else {
        // Use Joi validation for regular OPD visit types
        const { error } = ConsultationFormSchema.validate(formDataWithId, { abortEarly: false });
        if (error) {
          setErrors(joiDetailsToFieldErrors(error.details));
          toast.error(REQUIRED_FIELDS_TOAST);
          return;
        }
      }
    }

    // Payment section validation - only for non-OPD Old visit types
    if (!isFreeFollowUp) {
      if (
        !formData?.payment?.transactionType ||
        !formData?.payment?.paymentType ||
        formData?.payment?.amountPaid === "" ||
        confirmAmountPaid === ""
      ) {
        setErrors([
          "transactionType",
          "paymentType",
          "amountPaid",
          "amount",
        ]);
        toast.error(REQUIRED_FIELDS_TOAST);
        return;
      }
    } else {
      // For OPD Old visit type, ensure payment fields are cleared and skip validation
      setFormData(prev => ({
        ...prev,
        payment: {
          ...prev.payment,
          transactionType: null,
          paymentType: "",
          amountPaid: 0,
          remarks: "",
          transferBankAccount: ""
        }
      }));
      setConfirmAmountPaid('');
      setIsTransactionTypeSet(false);
    }

    setErrors([]);
    setIsLoading(true);
    try {
      const response = await createConsultationApi(formDataWithId);

      if (response?.code === 200) {
        toast.success(response?.message || "Consultation created successfully");
        navigate(`/patient/info/${patientId}`);
        return;
      }

      if (response?.isNetworkError) {
        toast.error(
          response?.message ||
            "No response from server. Please check your connection."
        );
      } else {
        toast.error(response?.message || "Some error occurred.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    async function fetchPatient() {
      if (patientId && patientId !== 'undefined' && patientId !== 'null') {
        setIsLoadingPatient(true);
        setIsErrorPatient(false);
        try {
          const response = await getPatientByIdApi({ patientId });
          if (response?.code === 200 && response?.data) {
            setPatientData(response.data);
          } else {
            setPatientData(null);
            setIsErrorPatient(true);
          }
        } catch (err) {
          setPatientData(null);
          setIsErrorPatient(true);
        } finally {
          setIsLoadingPatient(false);
        }
      }
    }
    fetchPatient();
  }, [patientId]);

  useEffect(() => {
    setFormData((preValue) => ({ ...preValue, UHID: patientData?.UHID }));
  }, [patientData]);

  // Function to check follow-up eligibility
  const checkFollowUpEligibility = async () => {
    const isoDateTime = getValidISODateTime(consultationDate, consultationTime);
    if (!formData?.patientId || !formData?.doctor || !formData?.unit || !formData?.visitType || !isoDateTime) {
      return;
    }

    setIsCheckingFollowUp(true);
    try {
      const response = await checkFollowUpEligibilityApi({
        patientId: formData.patientId,
        consultationType: consultationType, // pass actual type OPD/IPD
        doctor: formData.doctor._id || formData.doctor,
        unit: formData.unit._id || formData.unit,
        visitType: formData.visitType._id || formData.visitType,
        consultationDateTime: isoDateTime
      });

      if (response?.success) {
        setIsValidFollowUp(response.data.isEligible);
        console.log("Follow-up eligibility check:", response.data);
      } else {
        setIsValidFollowUp(false);
        console.log("Follow-up eligibility check failed:", response);
      }
    } catch (error) {
      console.error("Error checking follow-up eligibility:", error);
      setIsValidFollowUp(false);
    } finally {
      setIsCheckingFollowUp(false);
    }
  };

  // Check follow-up eligibility when relevant fields change (OPD only)
  useEffect(() => {
    if (consultationType !== "OPD") return;
    if (formData?.patientId && formData?.doctor && formData?.unit && formData?.visitType && consultationDate && consultationTime) {
      checkFollowUpEligibility();
    }
  }, [consultationType, formData?.patientId, formData?.doctor, formData?.unit, formData?.visitType, consultationDate, consultationTime]);

  // When valid follow-up, allow zero amount but keep fields editable; don't auto-clear payment
  useEffect(() => {
    if (isFreeFollowUp) {
      // no forced changes; UI allows 0 amount and edits
    }
  }, [isFreeFollowUp]);

  // Monitor visit type changes to reset transaction state
  useEffect(() => {
    if (formData?.visitType?.name) {
      const isOPD = formData.visitType.name === "OPD Old" || 
                    formData.visitType.name === "Old OPD" ||
                    formData.visitType.name === "Old" ||
                    formData.visitType.name?.toLowerCase().includes("opd old") ||
                    formData.visitType.name?.toLowerCase().includes("old");
      
      if (isOPD) {
        setIsTransactionTypeSet(false);
      }
    }
  }, [formData?.visitType?.name]);

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box sx={{}}>
          <Sidebar />
        </Box>
        {isLoadingPatient ? (
          <Loader />
        ) : isErrorPatient ? (
          <ErrorMessage />
        ) : (
          <Box
            component="form"
            noValidate
            onSubmit={handleSubmit}
            sx={{
              padding: 2,
              borderRadius: 2,
              marginRight: "90px",
              marginTop: 1,
              width: "100%",
            }}
          >
            <Button
              startIcon={<ArrowLeft />}
              onClick={() => navigate(-1)}
              sx={{
                color: '#5D6A82', 
                textTransform: 'none', 
                fontSize: '16px',
                fontWeight: 'bold',
                padding: 0,
                transition: 'none !important',
                marginBottom: '16px',
              }}
            >
              Back
            </Button>
            {/* Consultation Form */}
            <Typography
              sx={{
                fontFamily: 'Open Sans',
                fontWeight: 600,
                fontSize: '20px',
                lineHeight: '29px',
                letterSpacing: '0px',
                color: "#5D6A82",
                marginBottom: "1rem",
              }}
            >
              Enter Consultation Details
            </Typography>

            {/* Consultation Type */}
            <RadioGroup
              row
              value={consultationType || "OPD"}
              onChange={handleConsultationType}
              sx={{
                border: "1px solid lightblue",
                borderRadius: 2,
                padding: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginY: 2,
              }}
            >
              <Box sx={{ display: "flex", gap: 2 }}>
                <FormControlLabel value="OPD" control={<Radio />} label="OPD" />
                <FormControlLabel
                  value="Outsider"
                  control={<Radio />}
                  label="Outsider"
                />
                <FormControlLabel value="IPD" control={<Radio />} label="IPD" />
              </Box>
            </RadioGroup>

            {/* Patient Information */}
            <Grid container spacing={2} mb={3}>
              <Grid item xs={12} sm={4} md={3} xl={2}>
                <Box
                  sx={{
                    width: "100%",
                    height: "15rem",
                    border: "1px solid #5d6a82",
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    overflow: "hidden",
                    boxSizing: "border-box",
                    backgroundColor: "#f5f5f5",
                  }}
                >
                  {patientData?.patientImage ? (
                    <Box
                      component="img"
                      src={patientData.patientImage}
                      alt="Patient"
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <Typography
                      sx={{
                        color: "#999",
                        fontSize: "14px",
                        textAlign: "center",
                      }}
                    >
                      No picture
                    </Typography>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} sm={8} md={9} xl={10}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" color="grey">
                      Patient Information
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="UHID"
                      name="UHID"
                      value={patientData?.UHID || ""}
                      onChange={handleInputChange}
                      InputProps={{ readOnly: true }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Name"
                      name="name"
                      value={`${patientData?.firstName || ""} ${patientData?.lastName || ""}`}
                      onChange={handleInputChange}
                      InputProps={{ readOnly: true }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Contact Number"
                      name="phone"
                      value={patientData?.phone || ""}
                      onChange={handleInputChange}
                      InputProps={{ readOnly: true }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Gender"
                      name="gender"
                      value={patientData?.gender || ""}
                      onChange={handleInputChange}
                      InputProps={{ readOnly: true }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Address"
                      name="address"
                      value={patientData?.address || ""}
                      onChange={handleInputChange}
                      InputProps={{ readOnly: true }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="City"
                      name="city"
                      value={patientData?.city?.cityName || ""}
                      onChange={handleInputChange}
                      InputProps={{ readOnly: true }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="State"
                      name="state"
                      value={patientData?.state?.stateName || ""}
                      onChange={handleInputChange}
                      InputProps={{ readOnly: true }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Pincode"
                      name="pinCode"
                      value={patientData?.pinCode || ""}
                      onChange={handleInputChange}
                      InputProps={{ readOnly: true }}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            {/* Referral */}
            <Box
              sx={{
                padding: 2,
                backgroundColor: "#f5faff",
                borderRadius: 2,
                my: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 2,
                }}
              >
                <Typography variant="subtitle1" color="grey">
                  Referral
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <TextField
                    label="Name"
                    name="doctorName"
                    value={formData?.referral?.doctorName || ""}
                    onChange={(e) => handleInputChange(e, "referral")}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <TextField
                    label="Contact Number"
                    name="contactNumber"
                    value={formData?.referral?.contactNumber || ""}
                    onChange={(e) => handleInputChange(e, "referral")}
                    inputProps={phoneTextFieldInputProps}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <TextField
                    label="Reason for Referral"
                    name="reason"
                    value={formData?.referral?.reason || ""}
                    onChange={(e) => handleInputChange(e, "referral")}
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <DatePicker
                    label="Date of Referral"
                    slotProps={{ textField: { fullWidth: true } }}
                    value={
                      formData?.referral?.date
                        ? dayjs(formData.referral.date)
                        : null
                    }
                    onChange={(newValue) =>
                      handleInputChange(
                        {
                          target: { name: "date", value: newValue },
                        },
                        "referral"
                      )
                    }
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Consultation Information */}
            {consultationType === "OPD" && (
              <Box
                sx={{
                  padding: 2,
                  backgroundColor: "#f5faff",
                  borderRadius: 2,
                  my: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 2,
                  }}
                >
                  <Typography variant="subtitle1" color="grey">
                    Consultation Information
                  </Typography>
                  <Box>
                    <Button
                      variant="outlined"
                      sx={{ mr: 1, textTransform: "none" }}
                    >
                      Relief
                    </Button>
                  </Box>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Autocomplete
                      options={unitList || []}
                      getOptionLabel={(option) => option?.unitName || ""}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          label="Unit" 
                          required
                          InputLabelProps={{
                            required: true,
                            sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                border: errors?.includes('unit') ? '1px solid red' : '1px solid inherit',
                              },
                            },
                          }}
                        />
                      )}
                      value={formData?.unit || null}
                      onChange={(_, newValue) =>
                        handleDoctorUnitChange({
                          target: { name: "unit", value: newValue },
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Autocomplete
                      options={
                        formData?.unit
                          ? doctorsList.filter(
                              (d) =>
                                d?.role?.role === "Doctor" &&
                                d?.unit?._id === formData?.unit?._id
                            )
                          : doctorsList.filter(
                              (d) => d?.role?.role === "Doctor"
                            ) || []
                      }
                      getOptionLabel={(option) =>
                        `${option?.firstName || ""} ${
                          option?.lastName || ""
                        } (${option?.userId || ""})`
                      }
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          label="Doctor"
                          required
                          InputLabelProps={{
                            required: true,
                            sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                border: errors?.includes('doctor') ? '1px solid red' : '1px solid inherit',
                              },
                            },
                          }} 
                        />
                      )}
                      value={formData?.doctor || null}
                      onChange={(_, newValue) =>
                        handleDoctorUnitChange({
                          target: { name: "doctor", value: newValue },
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Autocomplete
                      options={visitTypeList || []}
                      getOptionLabel={(option) =>
                        `${option?.name || ""} (${option?.visitType || ""})`
                      }
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          label="Visit Type" 
                          required
                          InputLabelProps={{
                            required: true,
                            sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                border: errors?.includes('visitType') ? '1px solid red' : '1px solid inherit',
                              },
                            },
                          }}  
                        />
                      )}
                      value={formData?.visitType || null}
                      onChange={(_, newValue) => {
                        handleInputChange({
                          target: { name: "visitType", value: newValue },
                        });
                        if (!newValue?.allowsBackdate) {
                          const today = dayjs().format("YYYY-MM-DD");
                          setConsultationDate(today);
                          setBackdateReason("");
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <DatePicker
                      label="Date"
                      minDate={datePickerBounds.minDate}
                      maxDate={datePickerBounds.maxDate}
                      slotProps={{ textField: { fullWidth: true, required: true, InputLabelProps: { required: true, sx: { '& .MuiFormLabel-asterisk': { color: 'red' } } } } }}
                      value={consultationDate ? dayjs(consultationDate) : null}
                      onChange={(newValue) => {
                        const next = newValue
                          ? dayjs(newValue).format("YYYY-MM-DD")
                          : null;
                        setConsultationDate(next);
                        if (!isBackdatedConsultationDate(next)) {
                          setBackdateReason("");
                        }
                      }}
                    />
                  </Grid>
                  {showBackdateReason && (
                    <Grid item xs={12}>
                      <TextField
                        label="Reason for backdated consultation"
                        value={backdateReason}
                        onChange={(e) => setBackdateReason(e.target.value)}
                        required
                        fullWidth
                        multiline
                        minRows={2}
                        helperText="Required when consultation date is before today (compliance)."
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6} md={4}>
                    <TimePicker
                      label="Time"
                      slotProps={{ textField: { fullWidth: true, required: true, InputLabelProps: { required: true, sx: { '& .MuiFormLabel-asterisk': { color: 'red' } } } }, actionBar: { actions: [] } }}
                      value={
                        consultationTime
                          ? dayjs(consultationTime, "HH:mm")
                          : null
                      }
                      onChange={(newValue) =>
                        setConsultationTime(
                          newValue ? dayjs(newValue).format("HH:mm") : null
                        )
                      }
                      viewRenderers={{
                        hours: renderTimeViewClock,
                        minutes: renderTimeViewClock,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      type="number"
                      label="Amount"
                      name="amount"
                      value={amount}
                      onChange={e => setAmount(e.target.value === '' ? '' : Number(e.target.value))}
                      fullWidth
                      required={!isFreeFollowUp || Number(amount || 0) > 0}
                      InputLabelProps={{
                        required: !isFreeFollowUp || Number(amount || 0) > 0,
                        sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: errors?.includes('amount') ? '1px solid red' : '1px solid inherit',
                          },
                        },
                      }}
                    />
                    {isPotentialFollowUpVisitType && (
                      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        {isCheckingFollowUp ? (
                          <CircularProgress size={16} />
                        ) : (
                          <Typography 
                            variant="caption" 
                            color={isValidFollowUp ? "success.main" : "text.secondary"}
                            sx={{ fontStyle: 'italic' }}
                          >
                            {isValidFollowUp 
                              ? "✓ Valid follow-up (default 0; editable)" 
                              : "Not a valid follow-up (amount required)"
                            }
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* IPD Section */}
            {consultationType === "IPD" && (
              <>
                {/* Current Emergency Contact - IPD */}
                <Box
                  sx={{
                    padding: 2,
                    backgroundColor: "#f5faff",
                    borderRadius: 2,
                    marginBottom: 2,
                  }}
                >
                  <Typography variant="subtitle1" color="grey" marginBottom={2}>
                    Current Emergency Contact
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Name"
                        name="name"
                        value={formData?.emergencyContact?.name || ""}
                        onChange={(e) =>
                          handleInputChange(e, "emergencyContact")
                        }
                        required
                        InputLabelProps={{
                          required: true,
                          sx: { "& .MuiFormLabel-asterisk": { color: "red" } },
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              border: errors?.includes('name') ? '1px solid red' : '1px solid inherit',
                            },
                          },
                        }}  
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Autocomplete
                        options={relationshipList || []}
                        getOptionLabel={(option) =>
                          option?.relationshipName || ""
                        }
                        renderInput={(params) => (
                          <TextField 
                            {...params} 
                            label="Relationship"
                            required
                            InputLabelProps={{
                              ...params.InputLabelProps,
                              required: true,
                              sx: {
                                ...params.InputLabelProps?.sx,
                                "& .MuiFormLabel-asterisk": { color: "red" },
                              },
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                  border: errors?.includes("relationship") ? '1px solid red' : '1px solid inherit',
                                },
                              },
                            }}  
                          />
                        )}
                        value={formData?.emergencyContact?.relationship || null}
                        onChange={(_, newValue) =>
                          handleInputChange(
                            {
                              target: {
                                name: "relationship",
                                value: newValue,
                              },
                            },
                            "emergencyContact"
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Contact Number"
                        name="contactNumber"
                        value={formData?.emergencyContact?.contactNumber || ""}
                        onChange={(e) =>
                          handleInputChange(e, "emergencyContact")
                        }
                        required
                        InputLabelProps={{
                          required: true,
                          sx: { "& .MuiFormLabel-asterisk": { color: "red" } },
                        }}
                        inputProps={phoneTextFieldInputProps}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              border: errors?.includes("contactNumber") ? '1px solid red' : '1px solid inherit',
                            },
                          },
                        }}  
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* Doctor Allocation - IPD */}
                <Box
                  sx={{
                    py: 3,
                    px: 2,
                    backgroundColor: "#f5faff",
                    borderRadius: 2,
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle1" color="grey" mb={2}>
                    Doctor Allocation
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Autocomplete
                        options={unitList || []}
                        getOptionLabel={(option) => option?.unitName || ""}
                        renderInput={(params) => (
                          <TextField 
                            {...params} 
                            label="Unit"
                            required
                            InputLabelProps={{
                              ...params.InputLabelProps,
                              required: true,
                              sx: {
                                ...params.InputLabelProps?.sx,
                                "& .MuiFormLabel-asterisk": { color: "red" },
                              },
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                  border: errors?.includes("unit") ? '1px solid red' : '1px solid inherit',
                                },
                              },
                            }}  
                           />
                        )}
                        value={formData?.unit || null}
                        onChange={(_, newValue) =>
                          handleDoctorUnitChange({
                            target: {
                              name: "unit",
                              value: newValue,
                            },
                          })
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Autocomplete
                        options={
                          formData?.unit
                            ? doctorsList.filter(
                                (d) =>
                                  d?.role?.role === "Doctor" &&
                                  d?.unit?._id === formData?.unit?._id
                              )
                            : doctorsList.filter(
                                (d) => d?.role?.role === "Doctor"
                              ) || []
                        }
                        getOptionLabel={(option) =>
                          `${option?.firstName || ""} ${
                            option?.lastName || ""
                          } (${option?.userId || ""})`
                        }
                        renderInput={(params) => (
                          <TextField 
                            {...params} 
                            label="Doctor Name"
                            required
                            InputLabelProps={{
                              ...params.InputLabelProps,
                              required: true,
                              sx: {
                                ...params.InputLabelProps?.sx,
                                "& .MuiFormLabel-asterisk": { color: "red" },
                              },
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                  border: errors?.includes("doctor") ? '1px solid red' : '1px solid inherit',
                                },
                              },
                            }}  
                          />
                        )}
                        value={formData?.doctor || null}
                        onChange={(_, newValue) =>
                          handleDoctorUnitChange({
                            target: { name: "doctor", value: newValue },
                          })
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField label="Treatment Plan (Optional)" fullWidth />
                    </Grid>
                  </Grid>
                </Box>

                {/* Bed Allocation - IPD */}
                <Box
                  sx={{
                    px: 2,
                    py: 3,
                    backgroundColor: "#f5faff",
                    borderRadius: 2,
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle1" color="grey" mb={2}>
                    Bed Allocation
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Autocomplete
                        options={wardList || []}
                        getOptionLabel={(option) => option?.wardName || ""}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Ward Assignment"
                            required
                            InputLabelProps={{
                              ...params.InputLabelProps,
                              required: true,
                              sx: {
                                ...params.InputLabelProps?.sx,
                                "& .MuiFormLabel-asterisk": { color: "red" },
                              },
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                  border: errors?.includes("ward") ? '1px solid red' : '1px solid inherit',
                                },
                              },
                            }}  
                          />
                        )}
                        value={formData?.bedOccupancy?.ward || null}
                        onChange={(_, newValue) =>
                          handleInputChange(
                            {
                              target: {
                                name: "ward",
                                value: newValue,
                              },
                            },
                            "bedOccupancy"
                          )
                        }
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <Autocomplete
                        options={availableBedNumbers}
                        getOptionLabel={(option) => option?.bedNumber || ""}
                        renderInput={(params) => (
                          <TextField 
                            {...params} 
                            label="Bed Number"
                            required
                            InputLabelProps={{
                              ...params.InputLabelProps,
                              required: true,
                              sx: {
                                ...params.InputLabelProps?.sx,
                                "& .MuiFormLabel-asterisk": { color: "red" },
                              },
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                  border: errors?.includes("bed") ? '1px solid red' : '1px solid inherit',
                                },
                              },
                            }}   
                          />
                        )}
                        value={formData?.bedOccupancy?.bed || null}
                        onChange={(_, newValue) =>
                          handleInputChange(
                            {
                              target: {
                                name: "bed",
                                value: newValue,
                              },
                            },
                            "bedOccupancy"
                          )
                        }
                        disabled={!formData?.bedOccupancy?.ward?._id}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </>
            )}

            {/* Services - Outsider and IPD */}
            {consultationType !== "OPD" && (
              <Box
                sx={{
                  py: 3,
                  px: 2,
                  bgcolor: "#f5faff",
                  borderRadius: 2,
                  mb: 3,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1,
                    mb: 3,
                  }}
                >
                  <Typography variant="subtitle1" color="grey">
                    Services
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Button
                      variant="outlined"
                      sx={{ mr: 1, textTransform: "none" }}
                    >
                      Relief
                    </Button>
                    <Button
                      variant="outlined"
                      sx={{ textTransform: "none" }}
                      disabled={selectedServices?.filter(Boolean).length < 2}
                      onClick={handleBulkWaiverModalOpen}
                    >
                      Bulk Waiver
                    </Button>
                    <Button
                      variant="contained"
                      sx={{ textTransform: "none" }}
                      onClick={addServicesDetail}
                    >
                      Add
                    </Button>
                  </Box>
                </Box>

                <Box>
                  {formData?.services?.map((service, index) => (
                    <ServiceRow
                      key={index}
                      service={service}
                      index={index}
                      handleServicesChange={handleServicesChange}
                      categoryList={categoryList}
                      doctorsList={doctorsList}
                      formData={formData}
                      removeServicesDetail={removeServicesDetail}
                      selectedServices={selectedServices}
                      toggleRowSelection={toggleRowSelection}
                      consultationType={consultationType}
                      showCategoryError={errors.includes("category")}
                      showItemError={errors.includes("item")}
                    />
                  ))}

                  <Typography sx={{ textAlign: "right", fontWeight: "bold" }}>
                    Amount: ₹{formattedAmountToBePaid || 0}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Payment Information */}
            <Box
              sx={{
                padding: 2,
                backgroundColor: "#f5faff",
                borderRadius: 2,
                marginBottom: 2,
              }}
            >

              {/* Overall Discount */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginBottom: 2,
                }}
              >
                <Button
                  variant="outlined"
                  sx={{ textTransform: "none" }}
                  onClick={handleModalOpen}
                  disabled={!isTransactionTypeSet || isFreeFollowUp}
                >
                  Overall Discount
                </Button>

                <DiscountModal
                  open={modalOpen}
                  onClose={handleModalClose}
                  handleChange={handleInputChange}
                  formData={formData}
                  setFormData={setFormData}
                />
                <BulkWaiverModal
                  open={bulkWaiverModalOpen}
                  onClose={handleBulkWaiverModalClose}
                  handleServicesChange={handleServicesChange}
                  formData={formData}
                  setFormData={setFormData}
                  selectedServices={selectedServices}
                  setSelectedServices={setSelectedServices}
                />
              </Box>

              {/* Amount To Be Paid */}
              <Box
                sx={{
                  padding: 2,
                  backgroundColor: "#EAEAEA",
                  borderRadius: 2,
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                  Amount to be paid: ₹{formattedAmountToBePaid}
                </Typography>
                <Typography sx={{ fontWeight: "bold" }}>|</Typography>
                <Typography sx={{ fontWeight: "bold" }}>
                  Net Expense: ₹{netExpense}
                </Typography>
                {isPotentialFollowUpVisitType && !isValidFollowUp && (
                  <Typography variant="body2" color="warning.main" sx={{ ml: 2, fontStyle: 'italic' }}>
                    (Follow-up visit type selected but not eligible for free consultation)
                  </Typography>
                )}
              </Box>

              {/* Transaction Details */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete
                    options={transactionTypeList || []}
                    getOptionLabel={(option) => `${option?.name || ""} (${option?.paymentMethod})`}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Transaction Type"
                        required={!isFreeFollowUp}
                        sx={outlinedRequiredErrorSx(
                          errors?.includes("transactionType")
                        )}
                        InputLabelProps={{
                          required: !isFreeFollowUp,
                          sx: { "& .MuiFormLabel-asterisk": { color: "red" } },
                        }}
                      />
                    )}
                    value={formData?.payment?.transactionType || null}
                    onChange={(_, newValue) => {
                      // Don't allow changes if OPD Old visit type
                      if (isFreeFollowUp) return;

                      setErrors((prev) =>
                        prev.filter((k) => !PAYMENT_FIELD_ERROR_KEYS.includes(k))
                      );
                      setIsTransactionTypeSet(!!newValue);
                      handleInputChange(
                        {
                          target: {
                            name: "transactionType",
                            value: newValue,
                          },
                        },
                        "payment"
                      );
                      setFormData(prev => ({
                        ...prev,
                        payment: {
                          ...prev.payment,
                          transferBankAccount: "",
                          remarks: ""
                        }
                      }));
                    }}
                    disabled={isFreeFollowUp}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl
                    sx={{ minWidth: "100px" }}
                    required={!isFreeFollowUp}
                    fullWidth
                  >
                    <InputLabel
                      id="payment-type-id"
                      required={!isFreeFollowUp}
                      sx={{ "& .MuiFormLabel-asterisk": { color: "red" } }}
                    >
                      Payment Type
                    </InputLabel>
                    <Select
                      labelId="payment-type-id"
                      label="Payment Type"
                      name="paymentType"
                      value={formData?.payment?.paymentType || ""}
                      sx={outlinedRequiredErrorSx(errors?.includes("paymentType"))}
                      onChange={(e) => {
                        if (isFreeFollowUp) return;
                        handleInputChange(e, "payment");
                      }}
                      required={!isFreeFollowUp}
                      disabled={isFreeFollowUp}
                    >
                      <MenuItem value="InvoicePayment">Invoice Payment</MenuItem>
                      <MenuItem value="SelfPayment">Self Payment</MenuItem>
                      <MenuItem value="Refund">Refund</MenuItem>
                      <MenuItem value="IPDAdvance">IPD Advance</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Transfer Bank A/C"
                    name="transferBankAccount"
                    value={formData?.payment?.transferBankAccount || ""}
                    onChange={(e) => handleInputChange(e, "payment")}
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    type="number"
                    label="Amount (₹)"
                    name="amountPaid"
                    value={isFreeFollowUp ? 0 : (formData?.payment?.amountPaid === 0 ? '' : formData?.payment?.amountPaid)}
                    sx={outlinedRequiredErrorSx(
                      errors?.includes("amountPaid") || errors?.includes("amount")
                    )}
                    onChange={e => {
                       // Don't allow changes if OPD Old visit type
                       if (isFreeFollowUp) return;
                       handleInputChange({ target: { name: 'amountPaid', value: e.target.value === '' ? '' : Number(e.target.value) } }, 'payment');
                     }}
                     required={!isFreeFollowUp}
                     disabled={isFreeFollowUp}
                    InputLabelProps={{
                      required: !isFreeFollowUp,
                      sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                    }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    type="number"
                    label="Confirm Amount (₹)"
                    name="confirmAmountPaid"
                    value={isFreeFollowUp ? 0 : confirmAmountPaid}
                    onChange={e => {
                       // Don't allow changes if OPD Old visit type
                       if (isFreeFollowUp) return;
                       setConfirmAmountPaid(e.target.value === '' ? '' : Number(e.target.value));
                     }}
                     required={!isFreeFollowUp}
                     disabled={isFreeFollowUp}
                    InputLabelProps={{
                      required: !isFreeFollowUp,
                      sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                    }}
                    fullWidth
                    error={!amountsMatch && confirmAmountPaid !== "" && !isFreeFollowUp}
                    helperText={
                      !amountsMatch && confirmAmountPaid !== "" && !isFreeFollowUp
                        ? (isPendingPayment || isAdvancePayment)
                          ? `Amount must be between ₹0 and ₹${formData?.payment?.amountPaid}`
                          : "Amount does not match!"
                        : ""
                    }
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          border: (errors?.includes('amountPaid') && !confirmAmountPaid && !isFreeFollowUp) ? '1px solid red' : '1px solid inherit',
                        },
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Remarks"
                    name="remarks"
                    value={formData?.payment?.remarks || ""}
                    onChange={(e) => handleInputChange(e, "payment")}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Box>
            {/* )} */}

            {/* Register Button */}
            <Box sx={{ textAlign: "center" }}>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                sx={{ padding: "10px 40px", textTransform: "none" }}
                disabled={isLoading}
              >
                {isLoading ? <CircularProgress size={24} /> : `Submit`}
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default ConsultationForm;
